import { KeyboardEvent, useEffect, useState, useRef, useMemo } from "react"
import { Checkbox as MuiCheckbox } from "@mui/material"
import FormFieldDef from "../../definitions/fieldDef" // Assuming this is an array of objects
import { FormField } from '../../definitions/eFormFieldTypes'
import { ActionTypes } from "@/actions"
import { useDispatch, useSelector } from "react-redux"
import { useAuth } from "../auth/AuthContext"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button as ShadcnButton } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { fetchComponentsFromAPI } from "../api/componentsApi"
import { Label } from "@/components/ui/label"
import DebugFieldButton from "@/components/DebugFieldButton"
import RichTextEdit from "../editors/RichTextEdit"
import { getSystemRelationSource, getSystemRelationSources } from "../utils/systemRelationSources"
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const EMPTY_SELECT_VALUE = "__none__"

const capitalizeFirstCharacter = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const EditComponentField = ({
  initialFieldData,
  onFieldUpdated,
  autoFocusLabel = false,
  onLabelFocused,
}: {
  initialFieldData: any
  onNewField: any
  onFieldUpdated: any
  autoFocusLabel?: boolean
  onLabelFocused?: () => void
}) => {
  // Initialize fieldData with initialFieldData or a default value
  const [fieldData, setFieldData] = useState(initialFieldData || {})
  const [isNew, setIsNew] = useState(!initialFieldData)
  const [type, setType] = useState(initialFieldData?.type || '');
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const labelInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const [selectOptions, setSelectOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [lastAutoName, setLastAutoName] = useState<string | null>(null);
  const pendingLabelCaretRef = useRef<number | null>(null);

  // State for relation field configuration
  const { user: authUser } = useAuth();
  const reduxComponents = useSelector((state: any) => state.components.data ?? []);
  const [fetchedComponents, setFetchedComponents] = useState<any[]>([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [componentsError, setComponentsError] = useState<string | null>(null);
  const [selectedComponentSlug, setSelectedComponentSlug] = useState<string>('');
  const [selectedComponentFields, setSelectedComponentFields] = useState<any[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const isRelationType = (value?: string) =>
    value === 'relation' || value === 'multiplerelation' || value === 'systemrelation';
  const isInstructionType = (value?: string) => value === 'instruction';
  const systemRelationOptions = getSystemRelationSources();
  const userUid = useMemo(() => {
    const u =
      authUser?.uid ||
      (authUser as { info?: { uid?: string } })?.info?.uid;
    return u != null && String(u).trim() !== "" ? String(u).trim() : undefined;
  }, [authUser]);
  const relationComponents = useMemo(() => {
    const bySlug = new Map<string, any>();
    for (const comp of [...reduxComponents, ...fetchedComponents]) {
      const slug =
        typeof comp?.slug === "string"
          ? comp.slug.replace(/[!]+$/, "").trim()
          : "";
      if (slug) {
        bySlug.set(slug, { ...comp, slug });
      }
    }
    return Array.from(bySlug.values()).sort((a, b) =>
      String(a.name || a.slug).localeCompare(String(b.name || b.slug))
    );
  }, [reduxComponents, fetchedComponents]);
  const generateFieldName = (labelValue: string) =>
    labelValue.toLowerCase().replace(/\s+/g, '_');
  const resolveFieldName = (field: any) => {
    const currentName = typeof field?.name === 'string' ? field.name.trim() : '';
    if (currentName) {
      return currentName;
    }
    const labelValue = typeof field?.label === 'string' ? field.label.trim() : '';
    if (labelValue) {
      return generateFieldName(labelValue);
    }
    if (field?.uid) {
      return `field_${String(field.uid).replace(/[^a-z0-9_]/gi, '')}`;
    }
    return '';
  };

  const parseSelectOptions = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((option) => String(option).trim()).filter(Boolean);
    }
    if (typeof value === "string") {
      return value.split(",").map((option) => option.trim()).filter(Boolean);
    }
    return [];
  };

  useEffect(() => {
    if (!autoFocusLabel) return;
    const t = setTimeout(() => {
      labelInputRef.current?.focus();
      onLabelFocused?.();
    }, 0);
    return () => clearTimeout(t);
  }, [autoFocusLabel]);

  useEffect(() => {
    const caret = pendingLabelCaretRef.current;
    if (caret == null) {
      return;
    }
    const input = labelInputRef.current;
    if (!input || document.activeElement !== input) {
      pendingLabelCaretRef.current = null;
      return;
    }
    requestAnimationFrame(() => {
      try {
        input.setSelectionRange(caret, caret);
      } catch {
        // ignore selection errors for unsupported input states
      }
      pendingLabelCaretRef.current = null;
    });
  }, [fieldData.label]);

  useEffect(() => {
    setFieldData(initialFieldData || {})
    setIsNew(false) // Optionally reset isNew state based on the presence of initialFieldData

    // Initialize type from initialFieldData
    if (initialFieldData?.type) {
      setType(initialFieldData.type.toLowerCase());
    }

    // Initialize relation field state
    if (isRelationType(initialFieldData?.type)) {
      const value = initialFieldData.value || (initialFieldData?.type === 'systemrelation' ? 'forms' : '');
      setSelectedComponentSlug(value);

      // Parse existing fields
      if (initialFieldData.fields) {
        const fieldsArray = typeof initialFieldData.fields === 'string'
          ? initialFieldData.fields.split(',').map(f => f.trim()).filter(Boolean)
          : [];
        setSelectedFields(fieldsArray);
      }
    }

    const initialLabel = typeof initialFieldData?.label === 'string' ? initialFieldData.label.trim() : '';
    const initialName = typeof initialFieldData?.name === 'string' ? initialFieldData.name.trim() : '';
    if (initialLabel) {
      const autoName = generateFieldName(initialLabel);
      setLastAutoName(autoName);
      setNameManuallyEdited(initialName !== '' && initialName !== autoName);
    } else {
      setLastAutoName(null);
      setNameManuallyEdited(Boolean(initialName));
    }
  }, [initialFieldData]) // Dependency array, re-run the effect when initialFieldData changes

  useEffect(() => {
    const activeType = type || fieldData.type;
    if (activeType !== 'select' && activeType !== 'dropdown') {
      return;
    }
    const initialOptions = parseSelectOptions(fieldData.options ?? fieldData.value);
    setSelectOptions(initialOptions);
  }, [type, fieldData.type, fieldData.options, fieldData.value])

  useEffect(() => {
    const activeType = type || fieldData.type;
    if (activeType !== 'select' && activeType !== 'dropdown') {
      return;
    }
    const rawQuickSetValue = fieldData.quickSetValue;
    if (!rawQuickSetValue) {
      return;
    }
    const options = selectOptions.map((option) => {
      const [value, label] = option.includes(':') ? option.split(':') : [option, option];
      return { value: value.trim(), label: label.trim() };
    });
    const valueMatch = options.find((option) => option.value === rawQuickSetValue);
    if (valueMatch) {
      return;
    }
    const labelMatch = options.find((option) => option.label === rawQuickSetValue);
    if (labelMatch) {
      setFieldData((prevData: any) => ({ ...prevData, quickSetValue: labelMatch.value }));
      dispatch({
        type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
        payload: { name: 'quickSetValue', value: labelMatch.value, uid: fieldData.uid }
      });
    }
  }, [dispatch, fieldData.quickSetValue, fieldData.type, fieldData.uid, selectOptions, type])

  // Fetch components when type is relation (excluding system relations)
  useEffect(() => {
    if (
      !(isRelationType(type) || isRelationType(fieldData.type)) ||
      type === 'systemrelation' ||
      fieldData.type === 'systemrelation'
    ) {
      return;
    }

    let cancelled = false;
    setComponentsLoading(true);
    setComponentsError(null);
    fetchComponentsFromAPI({ userUid })
      .then((comps) => {
        if (!cancelled) {
          setFetchedComponents(comps);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to fetch components:', error);
          setComponentsError(
            error instanceof Error ? error.message : 'Failed to load collections'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setComponentsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [type, fieldData.type, userUid])

  useEffect(() => {
    if (!isRelationType(type) || type !== 'systemrelation') {
      return;
    }
    if (!selectedComponentSlug) {
      handleComponentChange('forms');
    }
  }, [type, selectedComponentSlug])

  // Fetch fields from selected component
  useEffect(() => {
    if (type === 'systemrelation' || fieldData.type === 'systemrelation') {
      const systemSource = getSystemRelationSource(selectedComponentSlug || 'forms');
      const systemFields = systemSource?.fields || [];
      setSelectedComponentFields(systemFields);
      const validFieldNames = systemFields.map((f: any) => f.name).filter(Boolean);
      const validSelectedFields = selectedFields.filter(f => validFieldNames.includes(f));
      if (validSelectedFields.length !== selectedFields.length) {
        setSelectedFields(validSelectedFields);
        if (validSelectedFields.length > 0) {
          const fieldsValue = validSelectedFields.join(', ');
          setFieldData((prevData: any) => ({ ...prevData, fields: fieldsValue }));
          dispatch({
            type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
            payload: { name: 'fields', value: fieldsValue, uid: fieldData.uid }
          });
        }
      }
      return;
    }

    if (selectedComponentSlug && relationComponents.length > 0) {
      const component = relationComponents.find(c => c.slug === selectedComponentSlug);
      if (component && component.fields) {
        console.log('Setting component fields:', { slug: selectedComponentSlug, fieldCount: component.fields.length, fields: component.fields });
        setSelectedComponentFields(component.fields);

        // Validate existing selectedFields against new component fields
        const validFieldNames = component.fields.map((f: any) => f.name).filter(Boolean);
        const validSelectedFields = selectedFields.filter(f => validFieldNames.includes(f));
        if (validSelectedFields.length !== selectedFields.length) {
          console.log('Filtering invalid selected fields:', {
            before: selectedFields,
            after: validSelectedFields,
            validFieldNames
          });
          setSelectedFields(validSelectedFields);
          if (validSelectedFields.length > 0) {
            const fieldsValue = validSelectedFields.join(', ');
            setFieldData((prevData: any) => ({ ...prevData, fields: fieldsValue }));
            dispatch({
              type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
              payload: { name: 'fields', value: fieldsValue, uid: fieldData.uid }
            });
          }
        }
      } else {
        console.log('No fields found for component:', selectedComponentSlug);
        setSelectedComponentFields([]);
      }
    } else {
      setSelectedComponentFields([]);
    }
  }, [selectedComponentSlug, relationComponents, type, fieldData.type, selectedFields, fieldData.uid, dispatch])

  // Handles input changes for form fields
  const handleInputChange = (e: any) => {
    const { name, value } = e.target
    if (e.target.name === 'type') {
      const newType = value.toLowerCase();
      setType(newType);
      setFieldData((prevData: any) => {
        const next = { ...prevData, type: newType };
        if (newType === 'code') {
          if (prevData.codeLength == null && prevData.codeLength !== 0) next.codeLength = 3;
          if (prevData.codePrefix == null) next.codePrefix = '';
          if (prevData.codeMode == null) next.codeMode = 'sequential';
          if (prevData.codeChars == null) next.codeChars = 'numeric';
        }
        return next;
      });

      // If changing to/from relation type, reset relation-specific state
      if (isRelationType(newType)) {
        // Don't reset if we're just initializing
        if (fieldData.type && fieldData.type !== newType) {
          setSelectedComponentSlug('');
          setSelectedFields([]);
        }
      }
    }
    // For checkboxes, use checked value; for other inputs, use the value
    const fieldValue = e.target.type === 'checkbox' ? e.target.checked : value

    // Auto-generate name from label (lowercase, spaces to underscores)
    // Only auto-generate if field is new (no existing name) or name is empty
    if (name === 'label' && value) {
      pendingLabelCaretRef.current = e.target.selectionStart ?? null;
      const normalizedLabel = capitalizeFirstCharacter(String(value));
      const existingName = fieldData.name || initialFieldData?.name || '';
      const generatedName = generateFieldName(normalizedLabel);
      const shouldGenerateName = !nameManuallyEdited || (lastAutoName && existingName === lastAutoName) || isNew;

      setFieldData((prevData: any) => ({
        ...prevData,
        [name]: normalizedLabel,
        // Only update name if we should generate it
        ...(shouldGenerateName ? { name: generatedName } : {})
      }))

      // Dispatch label update
      dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'label', value: normalizedLabel, uid: fieldData.uid } })

      // Only dispatch name update if we generated a new name
      if (shouldGenerateName) {
        setLastAutoName(generatedName);
        dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'name', value: generatedName, uid: fieldData.uid } })
      }
    } else {
      if (name === 'name') {
        setNameManuallyEdited(true);
        setLastAutoName(null);
      }
      setFieldData((prevData: any) => ({ ...prevData, [name]: fieldValue }))
      if (typeof fieldData.uid === 'undefined') {
        console.error('Field UID is missing')
      }
      if (e.target.type === 'checkbox') {
        dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: name, value: e.target.checked, uid: fieldData.uid } })
      } else {
        dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: name, value: value, uid: fieldData.uid } })
      }
    }
  }

  const updateSelectOptions = (options: string[]) => {
    setSelectOptions(options);
    setFieldData((prevData: any) => ({ ...prevData, options }));
    dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'options', value: options, uid: fieldData.uid } });
    if (fieldData.defaultOption && !options.includes(fieldData.defaultOption)) {
      setFieldData((prevData: any) => ({ ...prevData, defaultOption: '' }));
      dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'defaultOption', value: '', uid: fieldData.uid } });
    }
  };

  const addOptionFromInput = (rawOption: string) => {
    const trimmed = rawOption.trim();
    setNewOption('');
    if (!trimmed) {
      return;
    }
    if (selectOptions.some(option => option.toLowerCase() === trimmed.toLowerCase())) {
      return;
    }
    updateSelectOptions([...selectOptions, trimmed]);
  };

  const handleAddOption = () => {
    addOptionFromInput(newOption);
  };

  const handleNewOptionKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    addOptionFromInput(event.currentTarget.value);
  };

  const handleRemoveOption = (option: string) => {
    const nextOptions = selectOptions.filter(item => item !== option);
    updateSelectOptions(nextOptions);
  };

  const handleResetName = () => {
    const labelValue = typeof fieldData.label === 'string' ? fieldData.label.trim() : '';
    if (!labelValue) {
      return;
    }
    const generatedName = generateFieldName(labelValue);
    setFieldData((prevData: any) => ({ ...prevData, name: generatedName }));
    setNameManuallyEdited(false);
    setLastAutoName(generatedName);
    dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'name', value: generatedName, uid: fieldData.uid } });
  };

  const handleRichTextFieldChange = ({ name, value }: { name: string; value: string }) => {
    setFieldData((prevData: any) => ({ ...prevData, [name]: value }));
    dispatch({
      type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
      payload: { name, value, uid: fieldData.uid }
    });
  };

  // Handle component selection for relation fields
  const handleComponentChange = (componentSlug: string) => {
    setSelectedComponentSlug(componentSlug);
    // Don't reset selected fields - preserve them if the same component is selected
    // Only reset if switching to a different component
    if (selectedComponentSlug && selectedComponentSlug !== componentSlug) {
      setSelectedFields([]);
    }

    // Update the value field
    setFieldData((prevData: any) => ({ ...prevData, value: componentSlug }));
    dispatch({
      type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
      payload: { name: 'value', value: componentSlug, uid: fieldData.uid }
    });
  }

  // Handle field selection for relation fields
  const handleFieldToggle = (fieldName: string) => {
    if (!fieldName) {
      console.warn('handleFieldToggle: fieldName is empty');
      return;
    }

    const newSelectedFields = selectedFields.includes(fieldName)
      ? selectedFields.filter(f => f !== fieldName)
      : [...selectedFields, fieldName];

    console.log('Field toggle:', { fieldName, wasSelected: selectedFields.includes(fieldName), newSelectedFields });

    setSelectedFields(newSelectedFields);

    // Update the fields property as comma-separated string
    const fieldsValue = newSelectedFields.join(', ');
    setFieldData((prevData: any) => ({ ...prevData, fields: fieldsValue }));
    dispatch({
      type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
      payload: { name: 'fields', value: fieldsValue, uid: fieldData.uid }
    });
  }

  // Resets fieldData to the default component definition and triggers onNewField callback
  const handleUpdateOrAddField = () => {
    const activeType = type || fieldData.type;
    const nextFieldData = { ...fieldData };

    if (isInstructionType(activeType)) {
      const currentLabel = typeof nextFieldData.label === 'string' ? nextFieldData.label.trim() : '';
      if (!currentLabel) {
        nextFieldData.label = 'Instruction';
      }
      const currentName = typeof nextFieldData.name === 'string' ? nextFieldData.name.trim() : '';
      if (!currentName && nextFieldData.uid) {
        nextFieldData.name = `instruction_${String(nextFieldData.uid).replace(/[^a-z0-9_]/gi, '')}`;
      }
    }

    if (nextFieldData.label !== fieldData.label) {
      setFieldData((prevData: any) => ({ ...prevData, label: nextFieldData.label }));
      dispatch({
        type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
        payload: { name: 'label', value: nextFieldData.label, uid: fieldData.uid }
      });
    }

    const normalizedName = resolveFieldName(nextFieldData);
    if (normalizedName && normalizedName !== fieldData?.name) {
      setFieldData((prevData: any) => ({ ...prevData, name: normalizedName }));
      dispatch({
        type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
        payload: { name: 'name', value: normalizedName, uid: fieldData.uid }
      });
    }
    onFieldUpdated()
  }

  const handleEditorKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!(event.metaKey || event.ctrlKey) || event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    handleUpdateOrAddField();
  };

  // Retrieves field value or a default value
  const getFieldValueFromNewField = (fieldKey: string, defaultValue = "") => {
    return fieldData[fieldKey] || defaultValue
  }

  const generateField = (field: any) => {
    if (field.key === 'label') {
      const activeType = type || fieldData.type;
      const isInstruction = isInstructionType(activeType);
      return (
        <Input
          ref={labelInputRef}
          name={field.key}
          value={isInstruction ? 'Instruction' : getFieldValueFromNewField(field.key)}
          onChange={handleInputChange}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          className="w-full field-input bg-white"
          style={{ backgroundColor: '#FFFFFF' }}
          disabled={isInstruction}
        />
      )
    }
    switch (field.type) {
      case "number":
        return (
          <Input
            name={field.key}
            type="number"
            value={getFieldValueFromNewField(field.key)}
            onChange={handleInputChange}
            className="w-full field-input bg-white"
            style={{ backgroundColor: '#FFFFFF' }}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )
      case "type":
        return (
          <ShadcnSelect
            open={typeSelectOpen}
            onOpenChange={setTypeSelectOpen}
            value={getFieldValueFromNewField(field.key) || ''}
            onValueChange={(newType) => {
              const v = newType.toLowerCase();
              setType(v);
              setFieldData((prev: any) => ({ ...prev, type: v }));
              dispatch({
                type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
                payload: { name: 'type', value: v, uid: fieldData.uid }
              });
              setTypeSelectOpen(false);
            }}
          >
            <SelectTrigger className="w-full bg-white border-gray-200" data-testid="field-type-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FormField)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([value, val]) => {
                  return (
                    <SelectItem
                      key={val}
                      value={String(val)}
                      onPointerUp={() => setTypeSelectOpen(false)}
                    >
                      {value}
                    </SelectItem>
                  );
                })}
            </SelectContent>
          </ShadcnSelect>
        )
      case "text":
        if (field.key === 'value' && isInstructionType(type || fieldData.type)) {
          return (
            <div className="min-h-[200px]">
              <RichTextEdit
                name={field.key}
                value={String(getFieldValueFromNewField(field.key, ''))}
                onChange={handleRichTextFieldChange}
              />
            </div>
          )
        }

        if (field.key === 'options' && (type === 'select' || type === 'dropdown' || fieldData.type === 'select' || fieldData.type === 'dropdown')) {
          return (
            <div className="space-y-3 p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-center gap-2">
                <Input
                  value={newOption}
                  onChange={(event) => setNewOption(event.target.value)}
                  onKeyDown={handleNewOptionKeyDown}
                  placeholder="Add an option"
                  className="w-full bg-white"
                />
                <ShadcnButton type="button" onClick={handleAddOption}>
                  Add
                </ShadcnButton>
              </div>
              {selectOptions.length === 0 ? (
                <div className="text-xs text-gray-500">No options added yet.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectOptions.map((option) => (
                    <Badge
                      key={option}
                      variant="secondary"
                      className="bg-white border border-gray-200 text-gray-700 flex items-center gap-2"
                    >
                      {option}
                      <button
                        type="button"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => handleRemoveOption(option)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )
        }

        if (field.key === 'quickSetValue' && (type === 'select' || type === 'dropdown' || fieldData.type === 'select' || fieldData.type === 'dropdown')) {
          const selectOptionPairs = selectOptions.map((option) => {
            const [value, label] = option.includes(':') ? option.split(':') : [option, option];
            return { value: value.trim(), label: label.trim() };
          });
          const quickSetVal = fieldData.quickSetValue ?? '';
          return (
            <div className="space-y-2">
              <ShadcnSelect
                value={quickSetVal === '' ? EMPTY_SELECT_VALUE : quickSetVal}
                onValueChange={(selectedValue) => {
                  const v = selectedValue === EMPTY_SELECT_VALUE ? '' : selectedValue;
                  setFieldData((prevData: any) => ({ ...prevData, quickSetValue: v }));
                  dispatch({
                    type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
                    payload: { name: 'quickSetValue', value: v, uid: fieldData.uid }
                  });
                }}
              >
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="No quick set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_SELECT_VALUE}>No quick set</SelectItem>
                  {selectOptionPairs.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </ShadcnSelect>
            </div>
          )
        }

        if (field.key === 'quickSetValue' && (type === 'checkbox' || fieldData.type === 'checkbox')) {
          const boolVal = fieldData.quickSetValue;
          const boolStr =
            boolVal === true || boolVal === 'true' || boolVal === 1 || boolVal === '1'
              ? 'true'
              : boolVal === false || boolVal === 'false' || boolVal === 0 || boolVal === '0'
                ? 'false'
                : '';
          return (
            <div className="space-y-2">
              <ShadcnSelect
                value={boolStr === '' ? EMPTY_SELECT_VALUE : boolStr}
                onValueChange={(selectedValue) => {
                  const v = selectedValue === 'true' ? true : selectedValue === 'false' ? false : '';
                  setFieldData((prevData: any) => ({ ...prevData, quickSetValue: v }));
                  dispatch({
                    type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
                    payload: { name: 'quickSetValue', value: v, uid: fieldData.uid }
                  });
                }}
              >
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="No quick set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_SELECT_VALUE}>No quick set</SelectItem>
                  <SelectItem value="true">Checked</SelectItem>
                  <SelectItem value="false">Unchecked</SelectItem>
                </SelectContent>
              </ShadcnSelect>
            </div>
          )
        }

        if (field.key === 'defaultOption' && (type === 'select' || type === 'dropdown' || fieldData.type === 'select' || fieldData.type === 'dropdown')) {
          const defaultOpt = fieldData.defaultOption ?? '';
          return (
            <div className="space-y-2">
              <ShadcnSelect
                value={defaultOpt === '' ? EMPTY_SELECT_VALUE : defaultOpt}
                onValueChange={(selectedValue) => {
                  const v = selectedValue === EMPTY_SELECT_VALUE ? '' : selectedValue;
                  setFieldData((prevData: any) => ({ ...prevData, defaultOption: v }));
                  dispatch({
                    type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
                    payload: { name: 'defaultOption', value: v, uid: fieldData.uid }
                  });
                }}
              >
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="No default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_SELECT_VALUE}>No default</SelectItem>
                  {selectOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </ShadcnSelect>
            </div>
          )
        }

        if (field.key === 'case' && (type === 'text' || fieldData.type === 'text')) {
          const caseValue = fieldData.case ?? '';
          return (
            <div className="space-y-2">
              <ShadcnSelect
                value={caseValue === '' ? EMPTY_SELECT_VALUE : caseValue}
                onValueChange={(selectedValue) => {
                  const v = selectedValue === EMPTY_SELECT_VALUE ? '' : selectedValue;
                  setFieldData((prevData: any) => ({ ...prevData, case: v }));
                  dispatch({
                    type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED,
                    payload: { name: 'case', value: v, uid: fieldData.uid }
                  });
                }}
              >
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="No capitalization rule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_SELECT_VALUE}>No capitalization rule</SelectItem>
                  <SelectItem value="lower">lowercase</SelectItem>
                  <SelectItem value="title">Title Case</SelectItem>
                  <SelectItem value="name">Name Case (First Last)</SelectItem>
                </SelectContent>
              </ShadcnSelect>
            </div>
          )
        }

        if (field.key === 'name') {
          return (
            <div className="flex flex-col gap-2">
              <Input
                name={field.key}
                value={getFieldValueFromNewField(field.key)}
                onChange={handleInputChange}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                className="w-full field-input bg-white"
                style={{ backgroundColor: '#FFFFFF' }}
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{nameManuallyEdited ? 'Custom name set' : 'Auto-generated from label'}</span>
                <button
                  type="button"
                  onClick={handleResetName}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Reset to auto-name
                </button>
              </div>
            </div>
          )
        }
        // Special handling for relation field configuration
        if (field.key === 'value' && (isRelationType(type) || isRelationType(fieldData.type))) {
          const showSystemOptions = type === 'systemrelation' || fieldData.type === 'systemrelation';
          const compSlug = selectedComponentSlug || '';
          const relationSelectOptions = [...relationComponents];
          if (
            compSlug &&
            !relationSelectOptions.some((comp) => comp.slug === compSlug)
          ) {
            relationSelectOptions.unshift({
              slug: compSlug,
              name: compSlug,
              uid: compSlug,
              fields: [],
            });
          }
          return (
            <div className="space-y-2">
              <ShadcnSelect
                value={compSlug === '' ? EMPTY_SELECT_VALUE : compSlug}
                onValueChange={(v) => handleComponentChange(v === EMPTY_SELECT_VALUE ? '' : v)}
              >
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="Select a component..." />
                </SelectTrigger>
                <SelectContent className="z-[100003]" position="popper" sideOffset={4}>
                  {showSystemOptions ? (
                    systemRelationOptions.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.label}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value={EMPTY_SELECT_VALUE}>Select a component...</SelectItem>
                      {relationSelectOptions.map((comp) => (
                        <SelectItem key={comp.uid || comp.slug} value={comp.slug}>
                          {comp.name} ({comp.slug})
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </ShadcnSelect>
              {componentsLoading && relationSelectOptions.length === 0 ? (
                <p className="text-xs text-gray-500">Loading collections...</p>
              ) : null}
              {componentsError && relationSelectOptions.length === 0 ? (
                <p className="text-xs text-red-600">{componentsError}</p>
              ) : null}
              {!componentsLoading &&
                !componentsError &&
                !showSystemOptions &&
                relationSelectOptions.length === 0 ? (
                <p className="text-xs text-gray-500">No collections available.</p>
              ) : null}
            </div>
          )
        }

        if (field.key === 'value' && (type === 'textarea' || fieldData.type === 'textarea')) {
          return (
            <textarea
              name={field.key}
              value={getFieldValueFromNewField(field.key) ?? ''}
              onChange={handleInputChange}
              placeholder={`Default ${field.label.toLowerCase()}`}
              className="w-full min-h-[100px] rounded-md border border-gray-200 px-3 py-2 text-sm bg-white"
              rows={4}
            />
          );
        }

        // Special handling for relation fields selection
        const isRelationFieldType = isRelationType(type) || isRelationType(fieldData.type);
        if (field.key === 'fields' && isRelationFieldType) {
          console.log('Rendering fields selector:', {
            fieldKey: field.key,
            type,
            fieldDataType: fieldData.type,
            selectedComponentSlug,
            selectedComponentFieldsCount: selectedComponentFields.length,
            selectedFieldsCount: selectedFields.length
          });

          if (!selectedComponentSlug) {
            return (
              <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded border border-gray-200">
                Please select a component first to choose fields
              </div>
            )
          }

          if (selectedComponentFields.length === 0) {
            return (
              <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded border border-gray-200">
                {relationComponents.length > 0 || !componentsLoading
                  ? 'No fields available in the selected component'
                  : 'Loading components...'}
              </div>
            )
          }

          return (
            <div className="space-y-2 p-3 bg-gray-50 rounded border border-gray-200 max-h-60 overflow-y-auto">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Select fields to display:
              </div>
              {selectedComponentFields.map((compField: any) => {
                const fieldName = compField.name;
                if (!fieldName) {
                  console.warn('Field missing name:', compField);
                  return null;
                }
                const isChecked = selectedFields.includes(fieldName);
                return (
                  <div key={compField.name || compField.uid} className="flex items-center space-x-2 py-1 hover:bg-gray-100 rounded px-1">
                    <MuiCheckbox
                      checked={isChecked}
                      onChange={(e, checked) => {
                        e.stopPropagation();
                        handleFieldToggle(fieldName);
                      }}
                      size="small"
                      className="text-blue-600"
                    />
                    <Label
                      className="text-xs font-medium text-gray-700 cursor-pointer flex-1"
                      onClick={(e) => {
                        e.preventDefault();
                        handleFieldToggle(fieldName);
                      }}
                    >
                      {compField.label || compField.name}
                      <span className="text-gray-500 ml-1">({compField.type})</span>
                    </Label>
                  </div>
                );
              })}
              {selectedFields.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <div className="text-xs text-gray-600 font-medium">
                    Selected: {selectedFields.join(', ')}
                  </div>
                </div>
              )}
            </div>
          )
        }

        switch (field.case) {
          case 'lower':
            return (
              <Input
                name={field.key}
                value={getFieldValueFromNewField(field.key)}
                onChange={handleInputChange}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                className="w-full lowercase field-input bg-white"
                style={{ backgroundColor: '#FFFFFF' }}
              />
            )
          default:
            return (
              <Input
                name={field.key}
                value={getFieldValueFromNewField(field.key)}
                onChange={handleInputChange}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                className="w-full field-input bg-white"
                style={{ backgroundColor: '#FFFFFF' }}
              />
            )
        }
      case "codeMode":
        return (
          <ShadcnSelect
            value={getFieldValueFromNewField(field.key) || 'sequential'}
            onValueChange={(v) => {
              setFieldData((prev: any) => ({ ...prev, codeMode: v }));
              dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'codeMode', value: v, uid: fieldData.uid } });
            }}
          >
            <SelectTrigger className="w-full bg-white border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="random">Random</SelectItem>
              <SelectItem value="sequential">Sequential (001, 002, ...)</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        );
      case "codeChars":
        return (
          <ShadcnSelect
            value={getFieldValueFromNewField(field.key) || 'numeric'}
            onValueChange={(v) => {
              setFieldData((prev: any) => ({ ...prev, codeChars: v }));
              dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_UPDATED, payload: { name: 'codeChars', value: v, uid: fieldData.uid } });
            }}
          >
            <SelectTrigger className="w-full bg-white border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="numeric">Numeric (0-9)</SelectItem>
              <SelectItem value="alphanumeric">Alphanumeric (0-9, A-Z)</SelectItem>
            </SelectContent>
          </ShadcnSelect>
        );
      case "checkbox":
        return (
          <div className="flex items-center space-x-3 p-2.5 bg-blue-50 rounded-lg border border-blue-200 checkbox-field">
            <MuiCheckbox
              name={field.key}
              checked={
                field.key === 'allowEmptyOption'
                  ? fieldData.allowEmptyOption !== false
                  : getFieldValueFromNewField(field.key) ? true : false
              }
              onChange={handleInputChange}
              className="text-blue-600"
              size="small"
            />
            <div className="flex-1">
              <div className="text-xs font-medium text-gray-700">
                {field.label}
              </div>
              {field.key === 'allowEmptyOption' && (
                <div className="text-xs text-gray-500 mt-0.5">
                  Include a blank choice so the stored value can be empty
                </div>
              )}
              {field.key === 'quickDefault' && (
                <div className="text-xs text-gray-500 mt-0.5">
                  When enabled, this field will be used as the default field for Quick Add
                </div>
              )}
              {field.key === 'quickSetValue' && (
                <div className="text-xs text-gray-500 mt-0.5">
                  Value to apply when Quick Add selects an existing item
                </div>
              )}
            </div>
          </div>
        )
      default:
        return (
          <Input
            name={field.key}
            value={getFieldValueFromNewField(field.key)}
            onChange={handleInputChange}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            className="w-full field-input bg-white"
            style={{ backgroundColor: '#FFFFFF' }}
          />
        )
    }
  }


  const getFieldDescription = (fieldKey: string) => {
    const isRelationField = isRelationType(type) || isRelationType(fieldData.type);

    switch (fieldKey) {
      case 'label':
        return 'Display name shown to users'
      case 'name':
        return 'Internal field name used in the database (auto-generated from label)'
      case 'type':
        return 'Data type and input method for this field'
      case 'case':
        return 'How text input should be capitalized (for text fields only)'
      case 'value':
        return isRelationField
          ? 'Select the component/table this relation field will reference'
          : 'Default value or options for this field'
      case 'options':
        return 'Options available for select fields'
      case 'allowEmptyOption':
        return 'When enabled, the list includes a blank option; choosing it stores an empty value. When disabled, only defined options are available.'
      case 'defaultOption':
        return 'Default value for select fields'
      case 'description':
        return 'Help text or description shown to users when filling out this field'
      case 'fields':
        return isRelationField
          ? 'Select which fields from the related component to display in the relation dropdown'
          : 'Related fields configuration'
      case 'sourceField':
        return 'Field that contains the related item UID (system relation only)'
      case 'index':
        return 'Display order in forms and tables'
      case 'quickDefault':
        return 'Use this field as the primary Quick Add field'
      case 'quickSetValue':
        return 'Value to set when Quick Add updates an existing item'
      case 'inlineAdjust':
        return 'Show inline plus/minus controls in tables'
      case 'codeLength':
        return 'Number of characters in the generated code (e.g. 3 for 001)'
      case 'codePrefix':
        return 'Optional prefix prepended to the code (e.g. INV- for INV-001)'
      case 'codeMode':
        return 'Random: unique random code. Sequential: first available number (001, 002, ...)'
      case 'codeChars':
        return 'Numeric: 0-9 only. Alphanumeric: 0-9 and A-Z'
      default:
        return 'Configure this field property'
    }
  }

  const getFieldIcon = (fieldKey: string) => {
    const isRelationField = isRelationType(type) || isRelationType(fieldData.type);

    switch (fieldKey) {
      case 'label':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        )
      case 'name':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        )
      case 'type':
        return (
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        )
      case 'case':
        return (
          <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19h4l2-6h4l2 6h4M7 13l3-8 3 8m1-2h4m-2-4v8" />
          </svg>
        )
      case 'value':
        if (isRelationField) {
          return (
            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          )
        }
        return (
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        )
      case 'options':
        return (
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )
      case 'defaultOption':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'allowEmptyOption':
        return (
          <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4M12 4v16" />
          </svg>
        )
      case 'description':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'fields':
        if (isRelationField) {
          return (
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          )
        }
        return (
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        )
      case 'sourceField':
        return (
          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10M7 12h4m-4 5h10M5 7h.01M5 12h.01M5 17h.01" />
          </svg>
        )
      case 'inlineAdjust':
        return (
          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m-6-6h12" />
          </svg>
        )
      case 'quickDefault':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'quickSetValue':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        )
      case 'codeLength':
      case 'codePrefix':
      case 'codeMode':
      case 'codeChars':
        return (
          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        )
    }
  }

  return (
    <>
      <div className="space-y-6 field-editor" onKeyDown={handleEditorKeyDown}>
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-2">
            {isNew ? "Add New Field" : "Edit Field"}
          </h3>
          <p className="text-xs text-gray-600">
            {isNew ? "Configure a new field for your component" : "Modify field settings and properties"}
          </p>
        </div>

        <div className="space-y-5">
          {[
            ...FormFieldDef.filter((field) => field.key === 'type'),
            ...FormFieldDef.filter((field) => field.key !== 'type'),
          ].map((field, index) => {
            // Check if field should be shown based on forType
            const currentType = type || fieldData.type || '';
            const normalizedType = currentType === 'dropdown' ? 'select' : currentType;
            const shouldShow = typeof field.forType === 'undefined' || field.forType === normalizedType;
            const hiddenForInstruction = normalizedType === 'instruction' &&
              (field.key === 'label' || field.key === 'description' || field.key === 'quickDefault' || field.key === 'quickSetValue');
            if (normalizedType === 'select' && field.key === 'value') {
              return null;
            }
            if (field.key === 'index') {
              return null;
            }
            if (hiddenForInstruction) {
              return null;
            }
            if (!shouldShow) return null;

            // Get related data for relation fields
            let relatedData = undefined
            if ((field.key === 'value' && (isRelationType(type) || isRelationType(fieldData.type))) && selectedComponentSlug) {
              const relatedComponent = relationComponents.find(c => c.slug === selectedComponentSlug)
              if (relatedComponent) {
                relatedData = {
                  component: relatedComponent,
                  selectedFields: selectedFields,
                  availableFields: selectedComponentFields
                }
              }
            }

            const isCheckboxField = field.type === 'checkbox'
            return (
              <div key={index} className="space-y-2">
                {!isCheckboxField && (
                  <>
                    <div className="flex items-center space-x-2">
                      {getFieldIcon(field.key)}
                      <label className="text-xs font-medium text-gray-700">
                        {field.label}
                      </label>
                      {field.key === 'quickDefault' && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs px-1.5 py-0.5">
                          Default
                        </Badge>
                      )}
                      {field.key === 'quickSetValue' && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5">
                          Quick Set
                        </Badge>
                      )}
                      <DebugFieldButton
                        fieldConfig={{
                          ...field,
                          fieldData: fieldData,
                          currentType: type || fieldData.type
                        }}
                        fieldValue={getFieldValueFromNewField(field.key)}
                        relatedData={relatedData}
                        label={field.label}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      {getFieldDescription(field.key)}
                    </div>
                  </>
                )}
                {generateField(field)}
              </div>
            )
          })}
        </div>

        {/* Quick Add Configuration Summary */}
        {(getFieldValueFromNewField('quickDefault') || getFieldValueFromNewField('quickSetValue')) && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs font-medium text-blue-900">Quick Add Configuration</span>
            </div>
            <div className="text-xs text-blue-800 space-y-1">
              {getFieldValueFromNewField('quickDefault') && (
                <div>• This field will be the primary Quick Add field</div>
              )}
              {getFieldValueFromNewField('quickSetValue') && (
                <div>• This field will be set when Quick Add updates an existing item</div>
              )}
            </div>
          </div>
        )}

        <div className="pt-4">
          <ShadcnButton
            onClick={handleUpdateOrAddField}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center space-x-2 text-sm py-2.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{isNew ? "Add Field" : "Save Changes"}</span>
          </ShadcnButton>
        </div>
      </div>
    </>
  )
}

export default EditComponentField
